import React, { useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, Image, FlatList } from "react-native";
import type { AthleteProps, BracketGroupProps, BracketProps, BracketRoundProps, CompetitionPlayerBracketProps, EventProps, MyPlayerProps, PlayerBracketPickProps, PlayerBracketProps, RoundEventProps, TeamProps } from '../../../types';
import { BracketApi, BracketComeptitionApi, BracketCompetitionHelpers } from '../../api';
import BracketRoundSection from './BracketRound';
import { Button, Text, View } from '../../../Components/Themed';
import { useColors } from '../../../constants/useColors';
import BracketNavigator from './BracketNavigator';
import { Checkbox, Icons } from '../../../Components';
import RoundEventCard from './RoundEvent';

type BracketPlayProps = {
    player?:MyPlayerProps
    view_mode?:'desktop'|'mobile',
    insets?:{ top:number, bottom:number, left:number, right:number },
    player_bracket_id?:string,
    bracket_id?:string,
    width:number,
    onRequestAuthenticate: () => void,
    onSelectContest?:(round_event:RoundEventProps, bracket:BracketProps, player_bracket_pick?:PlayerBracketPickProps, away_placeholder_pick?:PlayerBracketPickProps, home_placeholder_pick?:PlayerBracketPickProps) => void
    onChangePlayerBracket: (bracket_id:string) => void,
    onPlayerBracketLoad:(pb:PlayerBracketProps) => void,
    onPlayerBracketUpdate: (pb:PlayerBracketProps, cpbs:CompetitionPlayerBracketProps[]) => void,
}

const BracketPlay = ({ player, view_mode, width, bracket_id, onSelectContest, player_bracket_id, insets, onPlayerBracketLoad, onPlayerBracketUpdate, onChangePlayerBracket, onRequestAuthenticate }:BracketPlayProps) => {
    const Colors = useColors();
    const [ round_event_loading, setRoundEventLoading ] = useState<string|undefined>(undefined);
    const [ show_filter, setShowFilter ] = useState(false);
    let round_margin = 4
    const [ final_four_size, setFinalFourSize ] = useState({ height: 0, width:0 });
    const [ bracket_size, setBracketSize ] = useState({ height:0, width:0, x: 0, y: 0 });
    const [ silent_round, setSilentRound ] = useState<BracketRoundProps>();
    const [ play_data, setPlayData ] = useState<{
        play_loading:boolean,
        player_bracket?:PlayerBracketProps,
        player_bracket_picks:PlayerBracketPickProps[]
    }>({
        play_loading:false,
        player_bracket_picks: []
    });
    const [ bracket_data, setData ] = useState<{
        loading:boolean,
        bracket?:BracketProps,
        bracket_groups:BracketGroupProps[],
        bracket_rounds:BracketRoundProps[],
        round_events:RoundEventProps[],
        teams:TeamProps[],
        athletes:AthleteProps[],
        selected_group?:BracketGroupProps,
        selected_round?:BracketRoundProps,
        events:EventProps[]
    }>({
        loading:false,
        bracket_groups: [],
        bracket_rounds: [],
        round_events: [],
        teams: [],
        athletes: [],
        events: []
    })
    const { play_loading, player_bracket, player_bracket_picks } = play_data;
    const { loading, bracket_groups, selected_group, bracket_rounds, round_events, bracket, teams, athletes, selected_round } = bracket_data;
    const no_group_round_events =  [ ...new Set(round_events.filter(ffe => ffe.bracket_group_id == '0').map(re => re.bracket_round_id)) ];
    const no_group_rounds = bracket_rounds.filter(br => no_group_round_events.includes(br.bracket_round_id));
    const { round_statuses, group_statuses } = BracketCompetitionHelpers.getPlayerBracketStatus(bracket_rounds, bracket_groups, round_events, player_bracket_picks);

    let visible_round_events = [ ...round_events ];
    if(width < 1000 && !selected_round && silent_round && silent_round.round_number > 1){
        let back_rounds = bracket_rounds.filter(br => br.round_number < silent_round.round_number).map(br => br.bracket_round_id.toString())
        visible_round_events = visible_round_events.filter(re => !back_rounds.includes(re.bracket_round_id.toString()))
    }

    const champion_team = player_bracket?.champion_id_type == 'team' ? teams.find(t => t.team_id == player_bracket?.champion_id) : undefined
    const champion_athlete = player_bracket?.champion_id_type == 'athlete' ? athletes.find(a => a.athlete_id == player_bracket?.champion_id) : undefined

    let visible_rounds = bracket_rounds
    if(selected_round){
        let next_round = bracket_rounds.findIndex(r => r.bracket_round_id == selected_round.bracket_round_id);
        visible_rounds = bracket_rounds.filter((_, i) => i == next_round || i == next_round + 1)
    }

   let round_width = BracketCompetitionHelpers.calcRoundWidth(width, bracket_rounds.length, round_margin, false)
    const full_width = BracketCompetitionHelpers.calcFullBracketSize(round_width, (bracket_rounds.length - no_group_rounds.length), 8);
   const final_four_events = BracketCompetitionHelpers.getNonGroupGames(round_events, bracket_groups, player_bracket_picks, teams)
   const left_final_four = final_four_events.find(ffe => ffe.orientation == 'left');
   const right_final_four = final_four_events.find(ffe => ffe.orientation == 'right');
   const championship = final_four_events.find(ffe => ffe.championship);

   const round_actions = BracketCompetitionHelpers.calcRoundActions(bracket_rounds, round_width, 0);


   useEffect(() => {
        if(!bracket_id){ return }
        getData(bracket_id)
    },[bracket_id])

    useEffect(() => {
        if(!player_bracket_id && !player_bracket){ return }
        if(!player_bracket_id && player_bracket){
            return setPlayData({ ...play_data, player_bracket: undefined, player_bracket_picks: [] })
        }
        if(!player_bracket_id){ return }
        getPlayData(player_bracket_id);
    },[player_bracket_id])

    const getPlayData = async(id:string) => {
        setPlayData({ ...play_data, play_loading: true })
        const resp = await BracketComeptitionApi.getPlayerBracketById(id)
        onPlayerBracketLoad(resp.player_bracket);
        setPlayData({
            ...play_data,
            play_loading:false,
            player_bracket_picks: resp.player_bracket_picks,
            player_bracket:resp.player_bracket
        })
    }

    const handlePick = async(pick:PlayerBracketPickProps) => {
        if(!BracketCompetitionHelpers.canMakePick(bracket)){ return alert('Bracket as already started') }
        if(!player){ return onRequestAuthenticate() }
        if(!bracket_id){ return }
        setRoundEventLoading(pick.round_event_id);
        let new_pick:PlayerBracketPickProps = { ...pick, bracket_id: bracket_id }
        if(!player_bracket){
            const new_pb = await BracketComeptitionApi.createPlayerBracket({
                player_bracket_id: '',
                player_id: '',
                bracket_name: `${player.username} Bracket ${Math.floor(Math.random() * (100 - 1 + 1) + 1)}`,
                create_datetime: '', last_update_datetime: '',
                bracket_id: bracket_id,
                completion_pct: 0,
                completion_status: 'inprogress',
                status: 'active'
            })
            new_pick.player_bracket_id = new_pb.player_bracket_id
        } else {
            new_pick.player_bracket_id = player_bracket.player_bracket_id
        }
        const resp = await BracketComeptitionApi.savePick(new_pick);
        setPlayData({
            ...play_data,
            player_bracket: resp.player_bracket,
            player_bracket_picks: player_bracket_picks.filter(pbp => !resp.player_bracket_picks.find(npb => npb.player_bracket_pick_id == pbp.player_bracket_pick_id)).concat(resp.player_bracket_picks.filter(pb => pb.status != 'deleted'))
        })
        onPlayerBracketUpdate(resp.player_bracket, resp.competition_player_brackets)
        setRoundEventLoading(undefined)
    }
    const getData = async(id:string) => {
        setData({ ...bracket_data, loading:true })
        const b = await BracketApi.getBracketById(id);
        if(!b){ return }
        let team_ids:string[] = []
        let athlete_ids:string[] = []
        b.round_events.map(re => {
            if(re.away_side_type == 'team' && re.away_side_id){ team_ids.push(re.away_side_id) }
            if(re.home_side_type == 'team' && re.home_side_id){ team_ids.push(re.home_side_id) }
            if(re.away_side_type == 'athlete' && re.away_side_id){ athlete_ids.push(re.away_side_id) }
            if(re.home_side_type == 'athlete' && re.home_side_id){ athlete_ids.push(re.home_side_id) }
        })
        let ts = await BracketApi.getTeamsByIds(team_ids);
        let aths = await BracketApi.getAthletesByIds(athlete_ids);
        setData({
            ...bracket_data,
            bracket: b.bracket,
            bracket_groups: b.bracket_groups,
            bracket_rounds: b.bracket_rounds,
            round_events: b.round_events,
            teams: ts,
            loading:false,
            athletes: aths
        })
    }

    const renderBracketGroups = (data: { item:BracketGroupProps, index:number, hide_titles?:boolean }) => {
        let sorted_rounds = [ ...bracket_rounds ].sort((a,b) => a.round_number - b.round_number);
        if(data.item.orientation == 'left'){ sorted_rounds = sorted_rounds.sort((a,b) => b.round_number - a.round_number) }
        sorted_rounds = sorted_rounds.filter(sr => !no_group_rounds.map(r => r.bracket_round_id.toString()).includes(sr.bracket_round_id.toString()))
        if(selected_group && selected_group.bracket_group_id != data.item.bracket_group_id){ return <></> }
        return (
            <View style={{ padding:4, marginRight: data.item.orientation == 'right' ? 50:0, marginLeft:data.item.orientation == 'left' ? 50 : 0 }}>
                <Text style={{ padding:5 }} size={14} weight='bold' textAlign={data.item.orientation == 'right' ? 'left' : 'right'}>{data.item.group_name}</Text>
                <View style={{alignItems:'flex-start' }}>
                    <FlatList
                        data={sorted_rounds}
                        keyExtractor={(item) => item.bracket_round_id.toString()}
                        horizontal
                        renderItem={({ item, index }) => renderBracketRounds({ 
                            item, 
                            index, 
                            bracket_group_id:data.item.bracket_group_id, 
                            orientation:data.item.orientation, 
                            hide_titles:data.hide_titles,
                        })}
                    />
                </View>
            </View>
        )
    }
    const renderBracketRounds = (data: { item:BracketRoundProps, index:number, bracket_group_id?:string, orientation:'left'|'right', hide_titles?:boolean, width?:number }) => {
        const selected = selected_round?.bracket_round_id == data.item.bracket_round_id ? true : false
        if(!bracket){ return <></> }
        const pick_status = round_statuses.find(rs => rs.bracket_round_id == data.item.bracket_round_id)?.status
        return (
                <BracketRoundSection 
                    round_event_loading={round_event_loading}
                    bracket_round={data.item}
                    round_events={visible_round_events}
                    selected={selected}
                    active_bracket_group_id={data.bracket_group_id || selected_group?.bracket_group_id}
                    bracket={bracket}
                    teams={teams} 
                    compact={false}
                    hide_titles={data.hide_titles}
                    pick_status={pick_status}
                    onSelectContest={(round_event, player_bracket_pick, away_placeholder_pick, home_placeholder_pick) => onSelectContest ? onSelectContest(round_event, bracket, player_bracket_pick, away_placeholder_pick, home_placeholder_pick):undefined}
                    athletes={athletes}
                    player_bracket_picks={player_bracket_picks}
                    width={data.width ?? round_width}
                    orientation={data.orientation}
                    onSelectRound={(br) => setData({ ...bracket_data, selected_round: selected_round?.bracket_round_id == br.bracket_round_id ? undefined: br })}
                    onPick={(pick) => handlePick(pick)}
                
                />
        )
    }

    const renderNavGroups = (data:{ item:BracketGroupProps, index:number }) => {
        const pick_status = group_statuses.find(s => s.bracket_group_id == data.item.bracket_group_id)?.status
        const selected = selected_group?.bracket_group_id == data.item.bracket_group_id ? true : false
        return (
            <Button type='text' style={{ flexDirection:'row', alignItems:'center', padding:10, margin:5 }} onPress={() => {
                setData({ ...bracket_data, selected_group: selected_group?.bracket_group_id == data.item.bracket_group_id ? undefined : data.item })

            }}>
                <View style={{ flexDirection:'row', alignItems:'center', flex:1, marginRight:25 }}>
                    <Checkbox checked={selected} color={Colors.text.action} disabled size={18} style={{ borderRadius:100 }} fill_style={{ borderRadius:100 }} onSelect={() => console.log('')} />
                    <Text style={{ marginLeft:10 }} size={14} theme='h1' textAlign="center">{data.item.group_name}</Text>
                </View>
                {pick_status == 'complete' ?
                <Icons.CheckCirlceIcon color={Colors.text.success} size={14} />
                :
                <Icons.AlertIcon color={Colors.text.warning} size={14} />
                }
            </Button>
        )
    }

    if(!bracket || loading || play_loading){
        return (
            <View type='blur'>
                <ActivityIndicator size='large' style={{ padding:20, alignSelf:'center' }} color={Colors.text.h1} />
            </View>
        )
    }

    return (
        <View transparent style={{ flex:1 }}>
            <BracketNavigator
                visible={show_filter}
                view_mode={view_mode}
                insets={insets}
                bracket_groups={bracket_groups}
                bracket_rounds={bracket_rounds}
                selected_group={selected_group}
                selected_round={selected_round}
                onSelectGroup={(g) => {
                    setData({ ...bracket_data, selected_group: selected_group?.bracket_group_id == g.bracket_group_id ? undefined : g })
                    setShowFilter(false)
                }}
                onSelectRound={(r) => {
                    setData({ ...bracket_data, selected_round: selected_round?.bracket_round_id == r.bracket_round_id ? undefined : r })
                    setShowFilter(false)
                }}
                onClose={() => setShowFilter(false)}
            /> 
            {player_bracket ?
            <View float style={{ padding:10, borderRadius:0, flexDirection:'row', alignItems:'center' }}>
                <Button
                    title='Change'
                    style={{ padding:10, marginRight:10}}
                    type='info'
                    title_color={Colors.text.action}
                    onPress={() => onChangePlayerBracket(bracket.bracket_id)}
                />
                {champion_team ?
                <View transparent style={{ justifyContent:'center', alignItems:'center', marginRight:10 }}>
                    <Image
                        source={{ uri: champion_team.image?.url }}
                        style={{ height:20, width:20, borderRadius:4 }}
                        resizeMode='cover'
                    />
                    <Text style={{ marginTop:2 }} size={12} theme='h1' textAlign='center'>{champion_team.abbr}</Text>
                </View>
                :champion_athlete ?
                <View transparent style={{ justifyContent:'center', alignItems:'center', marginRight:10 }}>
                    <Image
                        source={{ uri: champion_athlete.image?.url }}
                        style={{ height:20, width:20, borderRadius:4 }}
                        resizeMode='cover'
                    />
                    <Text style={{ marginTop:2 }} size={12} theme='h1' textAlign='center'>{champion_athlete.abbr_name}</Text>
                </View>
                :<></>}
                <View transparent style={{ flex:1 }}>
                    <Text size={14} theme='h1'>{player_bracket.bracket_name}</Text>
                    <Text style={{ marginTop:3 }} size={12} theme='description'>{(player_bracket.completion_pct*100).toFixed(2)}% Complete</Text>
                </View>
                <Button float style={{ padding:10, justifyContent:'center', alignItems:'center' }} onPress={() => setShowFilter(!show_filter)}>
                    <Icons.FilterIcon size={16} color={Colors.text.action}/>
                    <Text style={{ marginTop:3 }} theme='action'>FILTER</Text>
                </Button>
            </View>
            :
            <View style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                 <View transparent style={{ flex:1 }}>
                    <Text size={14} theme='h1'>No Bracket Selected</Text>
                    <Text style={{ marginTop:3 }} size={12} theme='description'>{0.00}% Complete</Text>
                </View>
                {!player ?
                <Button
                    style={{ padding:10 }}
                    title='Sign In'
                    onPress={() => onRequestAuthenticate()}
                    type='success'
                />
                :
                <Button
                    style={{ padding:10 }}
                    title='Select Bracket'
                    onPress={() => onChangePlayerBracket(bracket.bracket_id)}
                    type='success'
                />
                }
            </View>
            }
            
            <View transparent style={{ paddingLeft:10, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <FlatList
                    data={bracket_groups}
                    key={'group_list'}
                    horizontal
                    renderItem={renderNavGroups}
                    keyExtractor={(item) => item.bracket_group_id.toString()}
                />
            </View>
            <ScrollView>
                <ScrollView horizontal style={{ flexDirection:'row', padding:10 }} scrollEventThrottle={16} onScroll={(ev) => {
                        if(width >= 1000){ return }
                        const {contentOffset} = ev.nativeEvent;
                        const round_action = round_actions.find(ra => ra.location_start <= contentOffset.x && ra.location_end >= contentOffset.x);
                        if(round_action && (!silent_round || silent_round.bracket_round_id != round_action?.bracket_round.bracket_round_id)){
                            setSilentRound(round_action.bracket_round)
                        }
                }}>
                    <View transparent>
                    {width > 1000 && bracket_rounds.length > 5 && !selected_round && !selected_group ?
                    <View onLayout={(ev) => {
                        const { height, width, x, y } = ev.nativeEvent.layout;
                        setBracketSize({ height, width, x, y })
                    }}>
                        <View style={{ flex:1, width: width, flexDirection:'row', flexWrap:'wrap' }} >
                            <FlatList
                                numColumns={2}
                                data={bracket_groups.sort((a,b) => a.sequence - b.sequence)}
                                renderItem={({ item, index }) => renderBracketGroups({ item, index, hide_titles:item.sequence<3?false:true })}
                            />
                            { final_four_events.length > 0 ?
                            <View nativeID="final_four" 
                                float
                                style={{ position:'absolute',
                                padding:20, 
                                top: (bracket_size.height / 2) + (4*8) - (final_four_size.height / 2), 
                                left: (full_width / 2) - (final_four_size.width / 2),
                                flexDirection:'row', alignItems:'center', justifyContent:'center'}} onLayout={(ev) => {
                                const { height, width } = ev.nativeEvent.layout;
                                setFinalFourSize({ height, width })
                            }}>
                                {right_final_four ?
                                <View style={{ borderRadius:8, padding:2, borderBottomRightRadius:0, borderTopRightRadius:0 }}>
                                    <Text style={{ marginBottom:10 }} size={14} textAlign="center" weight='bold'>Final Four</Text>
                                    <RoundEventCard
                                        round_event={right_final_four.round_event}
                                        orientation={right_final_four.orientation}
                                        width={round_width}
                                        home_team={right_final_four.home_team}
                                        away_team={right_final_four.away_team}
                                        away_placeholder_team={right_final_four.away_pick_team}
                                        home_placeholder_team={right_final_four.home_pick_team}
                                        round_events={round_events}
                                        player_bracket_pick={right_final_four.player_bracket_pick}
                                        away_placeholder_pick={right_final_four.away_placeholder_pick}
                                        home_placeholder_pick={right_final_four.home_placeholder_pick}
                                        onPick={(player_bracket_pick) => handlePick(player_bracket_pick)}
                                    />
                                </View>
                                :<></>}
                                <View style={{ zIndex:1, borderRadius:8, margin:10 }}>
                                    <View style={{ padding:8, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                                        <Text size={16} textAlign="center" weight="bold">Championship</Text>
                                    </View>
                                    {championship ?
                                    <RoundEventCard
                                        round_event={championship.round_event}
                                        orientation={'right'}
                                        width={round_width}
                                        round_events={round_events}
                                        home_team={championship.home_team}
                                        away_team={championship.away_team}
                                        away_placeholder_team={championship.away_pick_team}
                                        home_placeholder_team={championship.home_pick_team}
                                        player_bracket_pick={championship.player_bracket_pick}
                                        away_placeholder_pick={championship.away_placeholder_pick}
                                        home_placeholder_pick={championship.home_placeholder_pick}
                                        onPick={(player_bracket_pick) => handlePick(player_bracket_pick)}
                                    />
                                    :<></>}
                                    {champion_team ?
                                    <View style={{ padding:10, flexDirection:'row', alignItems:'center' }}>
                                        <View style={{ marginRight:10 }}>
                                            <Image
                                                source={{ uri: champion_team.image?.url }}
                                                style={{ height:30, width:30 }}
                                                resizeMode="center"
                                            />
                                        </View>
                                        <View>
                                            <Text size={10}  weight="semibold">Champion Pick:</Text>
                                            <Text style={{ marginTop:4 }} size={14} weight="bold">{champion_team.market_name} {champion_team.name}</Text>
                                        </View>
                                    </View>
                                    :<></>}
                                </View>
                                {left_final_four ?
                                <View style={{ borderRadius:8, borderTopLeftRadius:0, borderBottomLeftRadius:0, padding:2  }}>
                                    <Text style={{ marginBottom:10 }} size={14} textAlign="center" weight='bold'>Final Four</Text>
                                    <RoundEventCard
                                        width={round_width}
                                        round_event={left_final_four.round_event}
                                        orientation={left_final_four.orientation}
                                        round_events={round_events}
                                        home_team={left_final_four.home_team}
                                        away_team={left_final_four.away_team}
                                        away_placeholder_team={left_final_four.away_pick_team}
                                        home_placeholder_team={left_final_four.home_pick_team}
                                        player_bracket_pick={left_final_four.player_bracket_pick}
                                        away_placeholder_pick={left_final_four.away_placeholder_pick}
                                        home_placeholder_pick={left_final_four.home_placeholder_pick}
                                        onPick={(player_bracket_pick) => handlePick(player_bracket_pick)}
                                    />
                                </View>
                                :<></>}
                            </View>
                            :<></>}
                        </View>
                    </View>
                    :
                    <View style={{ flexDirection:'row' }}>
                        {visible_rounds.sort((a,b) => a.round_number - b.round_number).map((br,i) => {
                            return renderBracketRounds({ item:br, index:i, orientation:'right' })
                        })}
                    </View>
                    }
                    </View>

                    
                    
                </ScrollView>
            </ScrollView>
           
        </View>
    )
}

export default BracketPlay


/*
                    <Button
                        title='Filter'
                        type='info'
                        onPress={() => setShowFilter(true)}
                    />
                    {show_filter ?
                    <View style={{ position:'absolute', top:5, right:5, borderRadius:8 }}>
                        <BracketNavigator
                            bracket_groups={bracket_groups}
                            bracket_rounds={bracket_rounds}
                            selected_group={selected_group}
                            selected_round={selected_round}
                            onSelectGroup={(g) => setData({ ...bracket_data, selected_group: selected_group?.bracket_group_id == g.bracket_group_id ? undefined : g })}
                            onSelectRound={(r) => setData({ ...bracket_data, selected_round: selected_round?.bracket_round_id == r.bracket_round_id ? undefined : r })}
                            onClose={() => setShowFilter(false)}
                        />
                    </View>
                    :<></>}
*/